// src/screens/mypage/EditProfileScreen.js
// 51차: 색 theme.js 단일화 — COLORS 참조 / 변경불가 안내 빨강 통일, 골드버튼 텍스트 ON_GOLD
// 74차: 이메일 인증 여부 안내 + 재전송 버튼 추가 (개인정보 섹션) — 이메일 가입 계정만 대상
// 74차: 화면 자체 커스텀 헤더 → 공통 AppHeader로 교체(섹션 제목은 본문 상단 텍스트로 유지)
// 76차: 비밀번호 변경 인라인 로직을 authService.js의 changePasswordWithReauth로 추출
// (강제 비밀번호 변경 화면에서도 재사용하기 위함, 동작 변화 없음)
// 78차: 닉네임/비밀번호/개인정보 저장 버튼(인라인 골드 fill)을 공용 GradientButton(Primary)으로,
// "인증 메일 재전송"/"초기화"/생년월일 모달 취소·확인을 TextButton으로 교체
import React, { useState, useRef, useEffect } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, TextInput, TouchableOpacity, StyleSheet,
  ScrollView, KeyboardAvoidingView,
  Platform, Modal, FlatList,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AppHeader from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { TextButton } from '../../components/common/Buttons';
import { updateProfile } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functionsAsia } from '../../services/firebase';
import { resendEmailVerification, changePasswordWithReauth, deleteAccount } from '../../services/authService';
import { unblockUser } from '../../services/blockService';
import { DangerButton } from '../../components/common/Buttons';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS } from '../../constants/theme';
import { PrivacyLockIcon, WarningIcon, EmailIcon, IconLabel } from '../../components/common/Icons';

const GOLD       = COLORS.gold;
const BG         = COLORS.bg;
const SURFACE    = COLORS.surface;
const SURFACE2   = COLORS.surface2;
const BORDER     = 'rgba(212,168,67,0.2)';
const TEXT       = COLORS.text;
const TEXT_SEC   = COLORS.textSec;
const TEXT_MUTED = 'rgba(255,255,255,0.35)';
const RED        = COLORS.red;

const BLOCKED_WORDS = ['관리자','어드민','admin','Admin','ADMIN','총관리','슈퍼','super','Super','SUPER'];
const SECTION_TITLE = { nickname:'닉네임 변경', password:'비밀번호 변경', personal:'개인정보 변경' };

// ── 날짜 상수 ──────────────────────────────────────────────────
const CUR_YEAR  = new Date().getFullYear();
const MIN_YEAR  = 1900;
const MAX_YEAR  = CUR_YEAR - 1; // 최소 1살
const YEARS     = Array.from({ length: MAX_YEAR - MIN_YEAR + 1 }, (_, i) => MAX_YEAR - i); // 최신년도 먼저
const MONTHS    = Array.from({ length: 12 }, (_, i) => i + 1);
const getDays   = (y, m) => new Date(y, m, 0).getDate(); // 해당 월 일수

// ── 스크롤 피커 컴포넌트 ───────────────────────────────────────
const ITEM_H = 48;
const VISIBLE = 5; // 보이는 항목 수

function ScrollPicker({ items, value, onChange, label, formatter }) {
  const flatRef = useRef(null);
  const idx     = items.indexOf(value);

  // 선택 시 스크롤 중앙 이동
  const scrollTo = (i) => {
    flatRef.current?.scrollToOffset({ offset: i * ITEM_H, animated: true });
  };

  const onScroll = (e) => {
    const offset = e.nativeEvent.contentOffset.y;
    const i = Math.round(offset / ITEM_H);
    const clamped = Math.max(0, Math.min(items.length - 1, i));
    if (items[clamped] !== value) onChange(items[clamped]);
  };

  return (
    <View style={pk.col}>
      {label ? <Text style={pk.colLabel}>{label}</Text> : null}
      <View style={pk.drum}>
        {/* 선택 하이라이트 */}
        <View style={pk.highlight} pointerEvents="none" />

        <FlatList
          ref={flatRef}
          data={items}
          keyExtractor={String}
          showsVerticalScrollIndicator={false}
          snapToInterval={ITEM_H}
          decelerationRate="fast"
          onMomentumScrollEnd={onScroll}
          onScrollEndDrag={onScroll}
          contentContainerStyle={{ paddingVertical: ITEM_H * Math.floor(VISIBLE / 2) }}
          getItemLayout={(_, i) => ({ length: ITEM_H, offset: ITEM_H * i, index: i })}
          initialScrollIndex={Math.max(0, idx)}
          onLayout={() => {
            if (idx > 0) scrollTo(idx);
          }}
          renderItem={({ item }) => {
            const selected = item === value;
            return (
              <TouchableOpacity
                style={pk.item}
                onPress={() => { onChange(item); scrollTo(items.indexOf(item)); }}
                activeOpacity={0.7}>
                <Text style={[pk.itemTxt, selected && pk.itemTxtOn]}>
                  {formatter ? formatter(item) : String(item)}
                </Text>
              </TouchableOpacity>
            );
          }}
        />
      </View>
    </View>
  );
}
const pk = StyleSheet.create({
  col:       { flex:1, alignItems:'center' },
  colLabel:  { color: TEXT_MUTED, fontSize:11, fontWeight:'700',
               letterSpacing:1, marginBottom:8 },
  drum:      { height: ITEM_H * VISIBLE, width:'100%', overflow:'hidden',
               position:'relative' },
  highlight: { position:'absolute', top: ITEM_H * Math.floor(VISIBLE / 2),
               left:0, right:0, height:ITEM_H,
               borderTopWidth:1, borderBottomWidth:1,
               borderColor: GOLD+'50', backgroundColor: GOLD+'0A',
               borderRadius:8, zIndex:1 },
  item:      { height:ITEM_H, justifyContent:'center', alignItems:'center' },
  itemTxt:   { color:TEXT_MUTED, fontSize:16, fontWeight:'500' },
  itemTxtOn: { color:TEXT, fontSize:18, fontWeight:'800' },
});

// ── 날짜 피커 모달 ──────────────────────────────────────────────
function BirthPicker({ visible, initialYear, initialMonth, initialDay, onConfirm, onClose }) {
  const [y, setY] = useState(initialYear || 1995);
  const [m, setM] = useState(initialMonth || 1);
  const [d, setD] = useState(initialDay || 1);

  const maxDay = getDays(y, m);
  const days   = Array.from({ length: maxDay }, (_, i) => i + 1);

  // 월/일 변경 시 유효성 보정
  const handleYearChange = (ny) => {
    setY(ny);
    const md = getDays(ny, m);
    if (d > md) setD(md);
  };
  const handleMonthChange = (nm) => {
    setM(nm);
    const md = getDays(y, nm);
    if (d > md) setD(md);
  };

  const handleConfirm = () => {
    const dateStr = `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    onConfirm(dateStr);
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <TouchableOpacity style={pm.overlay} activeOpacity={1} onPress={onClose} />
      <View style={pm.sheet}>
        <View style={pm.handle} />
        <View style={pm.header}>
          <TextButton label="취소" onPress={onClose} textStyle={pm.cancel} />
          <Text style={pm.title}>생년월일 선택</Text>
          <TextButton label="확인" onPress={handleConfirm} textStyle={pm.confirm} />
        </View>

        <View style={pm.pickerRow}>
          <ScrollPicker
            items={YEARS}
            value={y}
            onChange={handleYearChange}
            label="년"
            formatter={v => `${v}년`}
          />
          <ScrollPicker
            items={MONTHS}
            value={m}
            onChange={handleMonthChange}
            label="월"
            formatter={v => `${v}월`}
          />
          <ScrollPicker
            items={days}
            value={d}
            onChange={setD}
            label="일"
            formatter={v => `${v}일`}
          />
        </View>
      </View>
    </Modal>
  );
}
const pm = StyleSheet.create({
  overlay:   { flex:1, backgroundColor:'rgba(0,0,0,0.5)' },
  sheet:     { backgroundColor:SURFACE, borderTopLeftRadius:20, borderTopRightRadius:20,
               paddingBottom:40, borderTopWidth:1, borderColor:'rgba(212,168,67,0.2)' },
  handle:    { width:40, height:4, backgroundColor:'rgba(255,255,255,0.15)',
               borderRadius:2, alignSelf:'center', marginTop:12, marginBottom:4 },
  header:    { flexDirection:'row', alignItems:'center', justifyContent:'space-between',
               paddingHorizontal:20, paddingVertical:14,
               borderBottomWidth:1, borderBottomColor:'rgba(212,168,67,0.15)' },
  title:     { color:TEXT, fontSize:15, fontWeight:'800' },
  cancel:    { color:TEXT_MUTED, fontSize:14 },
  confirm:   { color:GOLD, fontSize:14, fontWeight:'800' },
  pickerRow: { flexDirection:'row', paddingHorizontal:16, paddingTop:12, gap:8 },
});

// ── 섹션 구분선 ───────────────────────────────────────────────
const Divider = ({ title }) => (
  <View style={dv.wrap}>
    <Text style={dv.title}>{title}</Text>
    <View style={dv.line} />
  </View>
);
const dv = StyleSheet.create({
  wrap:  { flexDirection:'row', alignItems:'center', gap:10, marginBottom:16, marginTop:8 },
  title: { color:GOLD, fontSize:13, fontWeight:'700', letterSpacing:0.5 },
  line:  { flex:1, height:1, backgroundColor:'rgba(212,168,67,0.2)' },
});

function Toggle({ value, onToggle }) {
  return (
    <TouchableOpacity onPress={onToggle} activeOpacity={0.8}
      style={[tog.track, value && tog.trackOn]}>
      <View style={[tog.thumb, value && tog.thumbOn]} />
    </TouchableOpacity>
  );
}
const tog = StyleSheet.create({
  track:   { width:48, height:28, borderRadius:14,
             backgroundColor:'rgba(255,255,255,0.15)',
             justifyContent:'center', paddingHorizontal:3 },
  trackOn: { backgroundColor:GOLD },
  thumb:   { width:22, height:22, borderRadius:11, backgroundColor:'#fff',
             alignSelf:'flex-start',
             shadowColor:'#000', shadowOffset:{ width:0, height:1 },
             shadowOpacity:0.3, shadowRadius:2, elevation:2 },
  thumbOn: { alignSelf:'flex-end' },
});

// 78차 42차: 차단 목록 관리 — 실수로 차단했을 때 되돌릴 방법이 없으면 안 되므로 추가.
// 80차 후속: [보안 수정] blockedUsers 닉네임 표시가 남의 users 문서를 직접 getDoc하던
// 것을(firestore.rules의 users.get()이 로그인만 하면 전면 허용이라 가능했음 — 이제
// 소유자+관리자 전용으로 좁혀서 이 경로는 막힘) getPublicProfilesCF로 이관 — 안전한
// 공개 필드(닉네임 등)만 반환, 한 번의 배치 호출로 N+1도 함께 해소.
function BlockedUsersList({ uids, myUid, onChanged }) {
  const [rows, setRows] = useState([]); // [{uid, nickname}]
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cf = httpsCallable(functionsAsia, 'getPublicProfilesCF');
        const result = await cf({ uids });
        const profileMap = {};
        (result.data.profiles || []).forEach(p => { profileMap[p.uid] = p; });
        return uids.map(uid => ({ uid, nickname: profileMap[uid]?.nickname || '(탈퇴한 회원)' }));
      } catch { return uids.map(uid => ({ uid, nickname: '(알 수 없음)' })); }
    })().then(r => { if (alive) setRows(r); });
    return () => { alive = false; };
  }, [uids.join(',')]);

  const handleUnblock = (uid, nickname) => {
    showAlert('차단 해제', `"${nickname}"의 차단을 해제할까요?`, [
      { text: '취소', style: 'cancel' },
      { text: '해제', onPress: async () => {
        try { await unblockUser(myUid, uid); await onChanged(); }
        catch { showAlert('오류', '차단 해제 중 오류가 발생했습니다.'); }
      }},
    ]);
  };

  return (
    <View style={{ gap: 8, marginBottom: 8 }}>
      {rows.map(({ uid, nickname }) => (
        <View key={uid} style={{
          flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
          paddingVertical: 10, paddingHorizontal: 14, borderRadius: 10,
          backgroundColor: 'rgba(255,255,255,0.04)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
        }}>
          <Text style={{ color: TEXT, fontSize: 13 }}>{nickname}</Text>
          <TouchableOpacity onPress={() => handleUnblock(uid, nickname)}>
            <Text style={{ color: GOLD, fontSize: 12, fontWeight: '700' }}>차단 해제</Text>
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );
}

// ── birth 문자열 파싱 헬퍼 ────────────────────────────────────
const parseBirth = (str) => {
  if (!str) return { y:1995, m:1, d:1 };
  const parts = str.split('-');
  return {
    y: parseInt(parts[0]) || 1995,
    m: parseInt(parts[1]) || 1,
    d: parseInt(parts[2]) || 1,
  };
};

// ── 메인 ──────────────────────────────────────────────────────
export default function EditProfileScreen({ navigation, route }) {
  const { user, profile, refreshProfile } = useAuth();
  const section = route?.params?.section || 'nickname';
  const adminRole = profile?.adminRole;
  const isSuperAdmin = adminRole === 'super';
  const isStoreAdmin = adminRole === 'store';
  // 33차: 매장관리자도 닉네임·개인정보 변경 불가
  const isAdminRestricted = isSuperAdmin || isStoreAdmin;

  // 총관리자 — 개인정보 변경 탭 접근 차단 (화면 내 안내로 표시)
  // goBack() 제거: 닉네임 변경과 동일하게 화면 내부에서 안내

  // 닉네임
  const [nickname,    setNickname]    = useState(profile?.nickname || user?.displayName || '');
  const [nicknameErr, setNicknameErr] = useState('');

  // 개인정보
  const [gender,       setGender]       = useState(profile?.gender || '');
  const [birth,        setBirth]        = useState(profile?.birth || '');
  const [bio,          setBio]          = useState(profile?.bio || '');
  const [statsPrivate, setStatsPrivate] = useState(profile?.statsPrivate ?? false);
  const [infoPrivate,   setInfoPrivate]   = useState(profile?.infoPrivate   ?? false);
  const [showPicker,   setShowPicker]   = useState(false);

  // 비밀번호
  const [currentPw, setCurrentPw] = useState('');
  const [newPw,     setNewPw]     = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  const [loading, setLoading] = useState(false);
  const [resendingVerify, setResendingVerify] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 74차: 이메일 가입 계정이면서 아직 인증 안 된 경우에만 안내 배너 표시
  const needsEmailVerify = profile?.provider === 'email' && auth.currentUser && !auth.currentUser.emailVerified;
  const handleResendVerification = async () => {
    try {
      setResendingVerify(true);
      await resendEmailVerification();
      showAlert('전송 완료', '인증 메일을 다시 보냈어요. 메일함을 확인해주세요.');
    } catch (e) {
      showAlert('오류', '인증 메일 전송에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally { setResendingVerify(false); }
  };

  const birthParsed = parseBirth(birth);
  const birthDisplay = birth
    ? `${birthParsed.y}년 ${birthParsed.m}월 ${birthParsed.d}일`
    : '선택 안함';

  // ── 닉네임 변경 ────────────────────────────────────────────
  const handleSaveNickname = async () => {
    const trimmed = nickname.trim();
    if (trimmed.length < 2)  { showAlert('알림', '닉네임은 2자 이상이어야 합니다.'); return; }
    if (trimmed.length > 12) { showAlert('알림', '닉네임은 12자 이하로 입력해주세요.'); return; }
    if (BLOCKED_WORDS.some(w => trimmed.includes(w))) {
      showAlert('사용 불가', '"관리자" 등의 단어는 닉네임에 사용할 수 없습니다.'); return;
    }
    try {
      setLoading(true);
      await updateProfile(auth.currentUser, { displayName: trimmed });
      await updateDoc(doc(db, 'users', user.uid), { nickname: trimmed });
      await refreshProfile();
      showAlert('완료', '닉네임이 변경되었습니다.', [
        { text:'확인', onPress:() => navigation.goBack() },
      ]);
    } catch (e) { showAlert('오류', e.message); }
    finally { setLoading(false); }
  };

  // ── 개인정보 저장 ──────────────────────────────────────────
  const handleSavePersonal = async () => {
    try {
      setLoading(true);
      await updateDoc(doc(db, 'users', user.uid), {
        gender, birth, bio: bio.trim(), statsPrivate, infoPrivate,
      });
      await refreshProfile();
      showAlert('완료', '개인정보가 저장되었습니다.', [
        { text:'확인', onPress:() => navigation.goBack() },
      ]);
    } catch (e) { showAlert('오류', e.message); }
    finally { setLoading(false); }
  };

  // ── 비밀번호 변경 ──────────────────────────────────────────
  const handleChangePassword = async () => {
    if (!currentPw || !newPw || !confirmPw) { showAlert('알림', '모든 항목을 입력해주세요.'); return; }
    if (newPw.length < 8) { showAlert('알림', '새 비밀번호는 8자 이상이어야 합니다.'); return; }
    if (newPw !== confirmPw) { showAlert('알림', '새 비밀번호가 일치하지 않습니다.'); return; }
    try {
      setLoading(true);
      await changePasswordWithReauth({ email: user.email, currentPassword: currentPw, newPassword: newPw });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      showAlert('완료', '비밀번호가 변경되었습니다.', [
        { text:'확인', onPress:() => navigation.goBack() },
      ]);
    } catch (e) {
      showAlert('오류', e.code === 'auth/wrong-password'
        ? '현재 비밀번호가 올바르지 않습니다.' : e.message);
    } finally { setLoading(false); }
  };

  // ── 78차 32차: 회원 탈퇴 ────────────────────────────────────
  const handleDeleteAccount = () => {
    showAlert(
      '회원 탈퇴',
      '탈퇴하면 보유 포인트가 모두 소멸되고 되돌릴 수 없어요.\n(작성한 리뷰·모집글은 유지되나 작성자 정보는 "탈퇴한 회원"으로 표시됩니다.)\n정말 탈퇴하시겠어요?',
      [
        { text:'취소', style:'cancel' },
        { text:'탈퇴', style:'destructive', onPress: async () => {
          try {
            setDeleting(true);
            await deleteAccount();
            // signOut 이후 AuthContext의 onAuthStateChanged가 감지해 자동으로 로그인 화면으로 이동
          } catch (e) {
            showAlert('탈퇴 실패', e.message || '탈퇴 처리에 실패했어요.');
          } finally {
            setDeleting(false);
          }
        } },
      ],
    );
  };

  return (
    <View style={s.container}>
      <AppHeader showBack />
      <Text style={s.screenTitle}>{SECTION_TITLE[section] || '설정'}</Text>

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={{ flex:1 }}>
        <ScrollView
          contentContainerStyle={s.body}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>

          {/* ══ 닉네임 변경 ══ */}
          {section === 'nickname' && (
            <>
              <Divider title="닉네임 변경" />
              {isAdminRestricted ? (
                <View style={s.disabledNotice}>
                  <IconLabel Icon={PrivacyLockIcon} size={s.disabledNoticeTitle.fontSize || 14} color={s.disabledNoticeTitle.color} textStyle={s.disabledNoticeTitle}>닉네임 변경 불가</IconLabel>
                  <Text style={s.disabledNoticeDesc}>
                    {isSuperAdmin ? '총관리자' : '매장관리자'} 계정은 닉네임을 변경할 수 없습니다.
                  </Text>
                  <Text style={s.disabledCurrentNickname}>
                    현재 닉네임: {profile?.nickname || '-'}
                  </Text>
                </View>
              ) : (
                <>
                  <Text style={s.label}>새 닉네임</Text>
                  <TextInput
                    style={s.input}
                    value={nickname}
                    onChangeText={v => {
                      setNickname(v);
                      setNicknameErr(BLOCKED_WORDS.some(w => v.includes(w))
                        ? '"관리자" 등의 단어는 사용 불가합니다.' : '');
                    }}
                    placeholder="2~12자"
                    placeholderTextColor={TEXT_MUTED}
                    maxLength={12}
                    autoFocus
                  />
                  <View style={{ flexDirection:'row', justifyContent:'space-between', marginTop:-10, marginBottom:14 }}>
                    {nicknameErr
                      ? <IconLabel Icon={WarningIcon} size={s.hintErr.fontSize || 11} color={s.hintErr.color} textStyle={s.hintErr}>{nicknameErr}</IconLabel>
                      : <Text style={s.hint}> </Text>}
                    <Text style={s.hint}>{nickname.length}/12자</Text>
                  </View>
                  <GradientButton
                    label={loading ? '변경 중...' : '닉네임 변경'}
                    onPress={handleSaveNickname}
                    disabled={loading || !!nicknameErr}
                    style={s.saveBtn}
                  />
                </>
              )}
            </>
          )}

          {/* ══ 비밀번호 변경 ══ */}
          {/* 79차: [버그 수정] SNS 로그인 계정(카카오/네이버/구글/애플)은 이메일/비밀번호
              자격증명 자체가 없어서, 이 폼으로 뭘 입력해 제출해도 재인증
              (EmailAuthProvider.credential 기반)이 항상 실패했음(전수 정독으로 발견) —
              MyPageScreen.js 메뉴에서 이 항목 자체를 이미 숨겼지만, 직접 이 화면으로
              진입할 수 있는 경로(딥링크 등)에 대비해 이 화면 자체에도 방어 추가. */}
          {section === 'password' && profile?.provider !== 'email' && (
            <>
              <Divider title="비밀번호 변경" />
              <Text style={s.hint}>
                {profile?.provider === 'kakao' ? '카카오' : profile?.provider === 'naver' ? '네이버'
                  : profile?.provider === 'google' ? '구글' : profile?.provider === 'apple' ? '애플' : 'SNS'}
                {' '}계정으로 로그인 중이라 앱 안에서 비밀번호를 변경할 수 없어요. 비밀번호 변경은 해당 서비스에서 진행해주세요.
              </Text>
            </>
          )}
          {section === 'password' && profile?.provider === 'email' && (
            <>
              <Divider title="비밀번호 변경" />
              <Text style={s.label}>현재 비밀번호</Text>
              <TextInput style={s.input} value={currentPw} onChangeText={setCurrentPw}
                secureTextEntry placeholder="현재 비밀번호 입력" placeholderTextColor={TEXT_MUTED} autoFocus />
              <Text style={s.label}>새 비밀번호</Text>
              <TextInput style={s.input} value={newPw} onChangeText={setNewPw}
                secureTextEntry placeholder="8자 이상" placeholderTextColor={TEXT_MUTED} />
              <Text style={s.label}>새 비밀번호 확인</Text>
              <TextInput style={s.input} value={confirmPw} onChangeText={setConfirmPw}
                secureTextEntry placeholder="새 비밀번호 재입력" placeholderTextColor={TEXT_MUTED} />
              {newPw && confirmPw && newPw !== confirmPw && (
                <IconLabel Icon={WarningIcon} size={s.hintErr.fontSize || 11} color={s.hintErr.color} textStyle={s.hintErr} style={{ marginTop:-10, marginBottom:8 }}>
                  비밀번호가 일치하지 않습니다.
                </IconLabel>
              )}
              <GradientButton
                label={loading ? '변경 중...' : '비밀번호 변경'}
                onPress={handleChangePassword}
                disabled={loading}
                style={s.saveBtn}
              />
            </>
          )}

          {/* ══ 개인정보 변경 ══ */}
          {section === 'personal' && (
            <>
              <Divider title="개인정보" />

              {isAdminRestricted ? (
                <View style={s.disabledNotice}>
                  <IconLabel Icon={PrivacyLockIcon} size={s.disabledNoticeTitle.fontSize || 14} color={s.disabledNoticeTitle.color} textStyle={s.disabledNoticeTitle}>개인정보 변경 불가</IconLabel>
                  <Text style={s.disabledNoticeDesc}>
                    {isSuperAdmin ? '총관리자' : '매장관리자'} 계정은 개인정보를 변경할 수 없습니다.
                  </Text>
                </View>
              ) : (
                <>
              {needsEmailVerify && (
                <View style={s.verifyBanner}>
                  <IconLabel Icon={EmailIcon} size={s.verifyBannerTxt.fontSize || 13} color={s.verifyBannerTxt.color} textStyle={s.verifyBannerTxt}>이메일 인증이 완료되지 않았어요.</IconLabel>
                  <TextButton
                    label={resendingVerify ? '전송 중...' : '인증 메일 재전송'}
                    onPress={handleResendVerification}
                    disabled={resendingVerify}
                    textStyle={s.verifyBannerBtn}
                  />
                </View>
              )}
              {/* 한줄 소개 */}
              <Text style={s.label}>한줄 소개</Text>
              <View>
                <TextInput
                  style={s.input}
                  value={bio}
                  onChangeText={setBio}
                  placeholder="ex) 공포 장르 좋아하는 탈출러 🔐"
                  placeholderTextColor={TEXT_MUTED}
                  maxLength={40}
                />
                <Text style={s.charCount}>{bio.length}/40</Text>
              </View>

              {/* 성별 */}
              <Text style={s.label}>성별</Text>
              <View style={s.genderRow}>
                {[{ key:'', label:'선택 안함' }, { key:'male', label:'남성' }, { key:'female', label:'여성' }].map(g => (
                  <TouchableOpacity
                    key={g.key}
                    style={[s.genderBtn, gender === g.key && s.genderBtnActive]}
                    onPress={() => setGender(g.key)}>
                    <Text style={[s.genderBtnText, gender === g.key && s.genderBtnTextActive]}>
                      {g.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* 생년월일 */}
              <Text style={s.label}>생년월일</Text>
              <TouchableOpacity style={s.birthField} onPress={() => setShowPicker(true)}>
                <Text style={birth ? s.birthValue : s.birthPlaceholder}>{birthDisplay}</Text>
                <Ionicons name="chevron-down" size={16} color={GOLD} />
              </TouchableOpacity>
              {birth ? (
                <TextButton
                  label="초기화"
                  onPress={() => setBirth('')}
                  style={{ alignSelf:'flex-end', marginTop:4, marginBottom:14, paddingVertical:0, paddingHorizontal:0 }}
                  textStyle={{ color:TEXT_MUTED, fontSize:11 }}
                />
              ) : <View style={{ marginBottom:14 }} />}

              <Divider title="정보 공개 설정" />

              {/* 프로필 카드 비공개 (내 마이페이지에서 bio+통계 숨기기) */}
              <View style={s.privacyCard}>
                <View style={{ flex:1 }}>
                  <Text style={s.privacyTitle}>프로필 카드 비공개</Text>
                  <Text style={s.privacySub}>
                    내 마이페이지에서 한줄 소개와 탈출 기록을 숨깁니다
                  </Text>
                </View>
                <Toggle value={infoPrivate} onToggle={() => setInfoPrivate(v => !v)} />
              </View>

              {/* 탈출 기록 공개 범위 (모집글 참가자 카드에서 숨기기) */}
              <View style={[s.privacyCard, { marginTop:10 }]}>
                <View style={{ flex:1 }}>
                  <Text style={s.privacyTitle}>탈출 기록 비공개</Text>
                  <Text style={s.privacySub}>
                    모집글 참가자 목록에서 도전·성공·성공률을 숨깁니다
                  </Text>
                </View>
                <Toggle value={statsPrivate} onToggle={() => setStatsPrivate(v => !v)} />
              </View>
              {(infoPrivate || statsPrivate) && (
                <View style={s.privacyNotice}>
                  <IconLabel Icon={PrivacyLockIcon} size={12} color={GOLD+'CC'} textStyle={{ color:GOLD+'CC', fontSize:12, lineHeight:18 }}>
                    {infoPrivate && statsPrivate
                      ? '마이페이지 카드와 모집글 기록 모두 비공개 상태입니다.'
                      : infoPrivate
                        ? '마이페이지의 소개·기록이 숨겨집니다.'
                        : '모집글에서 탈출 기록이 숨겨집니다.'}
                  </IconLabel>
                </View>
              )}

              <GradientButton
                label={loading ? '저장 중...' : '저장'}
                onPress={handleSavePersonal}
                disabled={loading}
                style={s.saveBtn}
              />

              {/* 78차 42차: 리뷰/크루 게시판에서 차단한 유저 목록 — 해제 기능 없이는
                  실수로 차단했을 때 되돌릴 방법이 없어서 추가 */}
              {profile?.blockedUsers?.length > 0 && (
                <>
                  <Divider title="차단 관리" />
                  <BlockedUsersList uids={profile.blockedUsers} myUid={user.uid} onChanged={refreshProfile} />
                </>
              )}

              <Divider title="계정 탈퇴" />
              <Text style={s.dangerZoneDesc}>
                탈퇴 시 보유 포인트가 소멸되며 되돌릴 수 없어요. 크루장인 경우 크루 관리
                화면에서 위임 또는 해체를 먼저 완료해야 탈퇴할 수 있어요.
              </Text>
              <DangerButton
                label={deleting ? '처리 중...' : '회원 탈퇴'}
                onPress={handleDeleteAccount}
                disabled={deleting}
                style={{ marginBottom:20 }}
              />
                </>
              )}
            </>
          )}

        </ScrollView>
      </KeyboardAvoidingView>

      {/* 생년월일 피커 모달 */}
      <BirthPicker
        visible={showPicker}
        initialYear={birthParsed.y}
        initialMonth={birthParsed.m}
        initialDay={birthParsed.d}
        onConfirm={(dateStr) => { setBirth(dateStr); setShowPicker(false); }}
        onClose={() => setShowPicker(false)}
      />
    </View>
  );
}

const s = StyleSheet.create({
  container:   { flex:1, backgroundColor:BG },
  screenTitle: { color:TEXT, fontSize:16, fontWeight:'800', paddingHorizontal:20, paddingTop:20 },
  body:        { padding:20, paddingBottom:60 },

  label:    { color:TEXT_SEC, fontSize:13, fontWeight:'600', marginBottom:8 },
  input:    { backgroundColor:'#0d0d1a', borderRadius:10, borderWidth:1,
              borderColor:BORDER, paddingHorizontal:14, height:50,
              color:TEXT, fontSize:14, marginBottom:14 },
  hint:     { fontSize:11, color:TEXT_MUTED },
  hintErr:  { fontSize:11, color:RED },
  charCount:{ color:TEXT_MUTED, fontSize:11, textAlign:'right', marginTop:-10, marginBottom:14 },

  genderRow:          { flexDirection:'row', gap:8, marginBottom:14 },
  genderBtn:          { flex:1, paddingVertical:10, borderRadius:10, backgroundColor:SURFACE,
                        alignItems:'center', borderWidth:1, borderColor:BORDER },
  genderBtnActive:    { backgroundColor:GOLD+'18', borderColor:GOLD },
  genderBtnText:      { color:TEXT_SEC, fontSize:13 },
  genderBtnTextActive:{ color:GOLD, fontWeight:'700' },

  // 생년월일 필드
  birthField:      { flexDirection:'row', alignItems:'center', justifyContent:'space-between',
                     backgroundColor:'#0d0d1a', borderRadius:10, borderWidth:1,
                     borderColor:BORDER, paddingHorizontal:14, height:50, marginBottom:4 },
  birthValue:      { color:TEXT, fontSize:14 },
  birthPlaceholder:{ color:TEXT_MUTED, fontSize:14 },

  privacyCard:   { flexDirection:'row', alignItems:'center', gap:12,
                   backgroundColor:SURFACE, borderRadius:12, borderWidth:1,
                   borderColor:BORDER, padding:14, marginBottom:10 },
  privacyTitle:  { color:TEXT, fontSize:14, fontWeight:'700', marginBottom:2 },
  privacySub:    { color:TEXT_MUTED, fontSize:12, lineHeight:17 },
  privacyNotice: { backgroundColor:GOLD+'0C', borderRadius:10, padding:12,
                   borderWidth:1, borderColor:GOLD+'25', marginBottom:14 },

  saveBtn:     { marginBottom:20 },
  dangerZoneDesc: { color:TEXT_MUTED, fontSize:12, lineHeight:18, marginBottom:14 },

  // 총관리자 변경 불가 안내
  disabledNotice:       { backgroundColor:COLORS.redBg, borderRadius:12,
                          borderWidth:1, borderColor:COLORS.redBorder,
                          padding:20, alignItems:'center', gap:8 },
  disabledNoticeTitle:  { color:RED, fontSize:15, fontWeight:'800' },
  disabledNoticeDesc:   { color:'rgba(255,255,255,0.5)', fontSize:13, textAlign:'center', lineHeight:20 },
  disabledCurrentNickname: { color:'rgba(255,255,255,0.3)', fontSize:12, marginTop:4 },

  // 74차: 이메일 인증 안내 배너
  verifyBanner:    { flexDirection:'row', alignItems:'center', justifyContent:'space-between',
                     backgroundColor:COLORS.warningBg, borderRadius:10, borderWidth:1,
                     borderColor:COLORS.warning, padding:12, marginBottom:16 },
  verifyBannerTxt: { color:COLORS.warning, fontSize:12, fontWeight:'700', flex:1, marginRight:8 },
  verifyBannerBtn: { color:COLORS.warning, fontSize:12, fontWeight:'800', textDecorationLine:'underline' },
});